import React, { useState } from 'react';
import { LanguagesIcon, MoonIcon, SunIcon } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { Card } from '../../components/ui/Panels';
import { Switch } from '../../components/ui/Field';
import { Button } from '../../components/ui/Button';
import { useTheme } from '../../contexts/ThemeContext';
import { useLocale } from '../../contexts/LocaleContext';
import { locales } from '../../data/locales';
import { cn } from '../../utils/cn';
export function Settings() {
  const {
    theme,
    setTheme
  } = useTheme();
  const {
    locale,
    setLocale,
    autoTranslate,
    setAutoTranslate,
    currencyCode,
    dir
  } = useLocale();
  const [emailQuotes, setEmailQuotes] = useState(true);
  const [emailMessages, setEmailMessages] = useState(true);
  const [pushReminders, setPushReminders] = useState(true);
  const [marketing, setMarketing] = useState(false);
  const [sharePhoto, setSharePhoto] = useState(true);
  return <AppShell title="Settings" subtitle="Language, appearance and what reaches you" data-id="element-2446" data-mp="4jia9mn18e1n">
      <div className="grid gap-6 lg:grid-cols-2" data-id="element-2447" data-mp="yw5hpiwsrxl1">
        <Card className="p-5" data-id="element-2448" data-mp="u3m26gejusdl">
          <div className="flex items-center gap-2" data-id="element-2449" data-mp="u2jcucwvkxzs">
            <LanguagesIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-2450" data-mp="p4rkjhsbj9v2" />
            <h2 className="font-display text-lg font-semibold text-ink" data-id="element-2451" data-mp="p2ojm2pmnwbk">Language &amp; region</h2>
          </div>
          <p className="mt-1 text-sm text-ink-2" data-id="element-2452" data-mp="1htge8vr1ocws">
            Changes the interface, currency, dates and units. Prices convert from Thai baht at the day’s rate.
          </p>

          <ul className="mt-4 grid gap-1.5 sm:grid-cols-2" data-id="element-2453" data-mp="1csivoi24hgur">
            {locales.map(option => {
            const active = option.code === locale;
            return <li key={option.code} data-id="element-2454" data-mp="13te2zsd8shqi">
                  <button type="button" aria-pressed={active} onClick={() => setLocale(option.code)} className={cn('flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left transition-colors duration-150 ease-out', active ? 'border-primary bg-primary-soft' : 'border-hairline hover:border-primary/40')} data-id="element-2455" data-mp="3slrkfvc2ge7q">
                    <span data-id="element-2456" data-mp="3l1yvgs1du37t">
                      <span className={cn('block text-sm', active ? 'font-semibold text-primary' : 'font-medium text-ink')} data-id="element-2457" data-mp="3l17asc9bppwb">
                        {option.label}
                      </span>
                      <span className="block text-[12px] text-ink-3" data-id="element-2458" data-mp="3c1sjxbp8vdba">{option.english}</span>
                    </span>
                    <span className="tnum shrink-0 text-[12px] text-ink-3" data-id="element-2459" data-mp="2xdz4ps42mct8">{option.currency}</span>
                  </button>
                </li>;
          })}
          </ul>

          <p className="mt-3 text-[12px] text-ink-3" data-id="element-2460" data-mp="2od0z55dzhx8j">
            Currently {currencyCode} · {dir === 'rtl' ? 'right-to-left layout' : 'left-to-right layout'}.
          </p>

          <div className="mt-2 border-t border-hairline" data-id="element-2461" data-mp="29ozo7nk4w2nx">
            <Switch checked={autoTranslate} onChange={setAutoTranslate} label="Auto-translate chat" description="Messages arrive in your language, with the original one tap away." data-id="element-2462" data-mp="29pj12puxg0v3" />
          </div>
        </Card>

        <Card className="p-5" data-id="element-2463" data-mp="2dpb3ektq9as0">
          <h2 className="font-display text-lg font-semibold text-ink" data-id="element-2464" data-mp="2dt7cjjmojglm">Appearance</h2>
          <p className="mt-1 text-sm text-ink-2" data-id="element-2465" data-mp="2smaggo7clvtv">Dark mode keeps the warm palette but drops the page to a charcoal brown.</p>
          <div className="mt-4 grid grid-cols-2 gap-2" data-id="element-2466" data-mp="31gff3tsq4bjy">
            {[{
            id: 'light' as const,
            label: 'Light',
            icon: <SunIcon className="h-4 w-4" aria-hidden="true" data-id="element-2467" data-mp="2xfwno0uqt7ia" />
          }, {
            id: 'dark' as const,
            label: 'Dark',
            icon: <MoonIcon className="h-4 w-4" aria-hidden="true" data-id="element-2468" data-mp="3bfecuhbyams6" />
          }].map(option => <button key={option.id} type="button" aria-pressed={theme === option.id} onClick={() => setTheme(option.id)} className={cn('flex items-center gap-2 rounded-lg border px-3.5 py-3 text-sm font-medium transition-colors duration-150 ease-out', theme === option.id ? 'border-primary bg-primary-soft text-primary' : 'border-hairline text-ink-2 hover:text-ink')} data-id="element-2469" data-mp="36hcvq1y34xjc">
                {option.icon}
                {option.label}
              </button>)}
          </div>

          <h3 className="mt-6 font-display text-base font-semibold text-ink" data-id="element-2470" data-mp="3gjqbtjkm1758">Notifications</h3>
          <div className="mt-1 divide-y divide-hairline" data-id="element-2471" data-mp="3lijrnindda8k">
            <Switch checked={emailQuotes} onChange={setEmailQuotes} label="Quotes and booking updates" description="Email" data-id="element-2472" data-mp="3lk4l14xy6n4u" />
            <Switch checked={emailMessages} onChange={setEmailMessages} label="New chat messages" description="Email" data-id="element-2473" data-mp="vvlsaye1p8bg" />
            <Switch checked={pushReminders} onChange={setPushReminders} label="Day-before reminders" description="Push — includes access notes and the kitchen checklist" data-id="element-2474" data-mp="2la3ikn3oafwo" />
            <Switch checked={marketing} onChange={setMarketing} label="New chefs near you" description="Occasional email" data-id="element-2475" data-mp="2bj8vxhmileap" />
          </div>

          <h3 className="mt-6 font-display text-base font-semibold text-ink" data-id="element-2476" data-mp="26jmhj4uy9s0b">Privacy</h3>
          <div className="mt-1 divide-y divide-hairline" data-id="element-2477" data-mp="22hsmbc3ixg7j">
            <Switch checked={sharePhoto} onChange={setSharePhoto} label="Show my photo to chefs" description="Your name is always visible; the photo is optional." data-id="element-2478" data-mp="1xjr0wkiml4sw" />
          </div>

          <div className="mt-6 flex flex-wrap gap-2 border-t border-hairline pt-4" data-id="element-2479" data-mp="1noz6tqo9502v">
            <Button type="button" variant="outline" size="sm" data-id="element-2480" data-mp="3mxmgaci27blz">
              Download my data
            </Button>
            <Button type="button" variant="ghost" size="sm" className="text-danger" data-id="element-2481" data-mp="3hysyw8znj0gj">
              Delete account
            </Button>
          </div>
        </Card>
      </div>
    </AppShell>;
}